import { PRESENCE_ACTIVITY_KINDS, PRESENCE_STEPS } from "@proef/domain";
import { z } from "zod";

/** The caller's own presence: `PUT /v1/presence` (the heartbeat), `GET` and `PATCH /v1/presence`.
 * While `enabled` is false the edge stores nothing about the person and nobody sees them. */
export const PresenceSelf = z.object({
  enabled: z.boolean(),
  lastSeenAt: z.iso.datetime().nullable(),
});
export type PresenceSelf = z.infer<typeof PresenceSelf>;

/**
 * What the heartbeat says the person is doing, sent only while a tasting is open on the device. A
 * solo tasting carries no id and no code, by construction: nothing that identifies it leaves the
 * device. `flightCode` is only read to find the flight; the edge never stores or returns it.
 */
export const PresenceActivityReport = z
  .object({
    kind: z.enum(PRESENCE_ACTIVITY_KINDS),
    sessionId: z.string().min(1).max(64).optional(),
    flightCode: z.string().min(1).max(32).optional(),
    /** When the tasting began on this device; the edge caps the show at 4 h after it. */
    startedAt: z.iso.datetime(),
    /** The guided page the person is on; ignored (never stored) for a solo tasting. */
    step: z.enum(PRESENCE_STEPS).optional(),
  })
  .refine(
    (report) =>
      report.kind === "solo"
        ? report.sessionId === undefined &&
          report.flightCode === undefined &&
          report.step === undefined
        : report.kind === "session"
          ? report.sessionId !== undefined && report.flightCode === undefined
          : report.flightCode !== undefined && report.sessionId === undefined,
    { message: "an activity names exactly what its kind needs, and a solo one nothing" },
  );
export type PresenceActivityReport = z.infer<typeof PresenceActivityReport>;

/**
 * `PUT /v1/presence` body, optional as a whole so a bundle from before activity keeps working:
 * absent or without `activity` leaves the stored activity alone, `null` ends it, an object sets it.
 */
export const PresenceHeartbeat = z.object({
  activity: PresenceActivityReport.nullable().optional(),
});
export type PresenceHeartbeat = z.infer<typeof PresenceHeartbeat>;

/** PATCH /v1/presence -- the settings switch. Off forgets the last sighting and any activity. */
export const PatchPresenceRequest = z.object({ enabled: z.boolean() });
export type PatchPresenceRequest = z.infer<typeof PatchPresenceRequest>;

export const PRESENCE_AUDIENCES = ["friends", "groups"] as const;
export const PresenceAudience = z.enum(PRESENCE_AUDIENCES);
export type PresenceAudience = z.infer<typeof PresenceAudience>;

export const JOIN_RIGHTS = ["participant", "group"] as const;

/**
 * A tasting someone is in right now. A solo one is only `{ kind, until }`; a shared one adds the
 * session's name. `sessionId` and `join` appear only when the caller may join: as a participant, or
 * as a member of the group the session is open to. Anyone else sees the name and no way in.
 */
export const PresenceActivity = z.object({
  kind: z.enum(PRESENCE_ACTIVITY_KINDS),
  name: z.string().exactOptional(),
  sessionId: z.string().min(1).exactOptional(),
  join: z.enum(JOIN_RIGHTS).exactOptional(),
  /** Where in the guided tasting a shared one is, as of the last heartbeat; never on a solo one. */
  step: z.enum(PRESENCE_STEPS).exactOptional(),
  /** The latest it can still show: 4 h after it began. */
  until: z.iso.datetime(),
});
export type PresenceActivity = z.infer<typeof PresenceActivity>;

/** Where a session someone said yes to stands: coming up, running, or just over. */
export const SESSION_PHASES = ["soon", "live", "after"] as const;

/**
 * One person the caller may see: an accepted friend, a co-member of a group, or both. `activity` and
 * `sessionPhase` are optional additions: a bundle that predates them reads the rest unchanged, and
 * `openVia` keeps its two values for the same reason.
 */
export const PresenceEntry = z.object({
  userId: z.string().min(1),
  /** Open to taste together right now. */
  open: z.boolean(),
  /** Why they are open: the app in the foreground within 4 h, or a session they said yes to. */
  openVia: z.enum(["app", "session"]).nullable(),
  lastSeenAt: z.iso.datetime().nullable(),
  /** Which of the caller's circles this person belongs to. */
  audience: z.array(PresenceAudience).min(1),
  /** Tasting right now; absent when they are not. */
  activity: PresenceActivity.exactOptional(),
  /** With `openVia: "session"`: whether that session is still ahead, running or just over. */
  sessionPhase: z.enum(SESSION_PHASES).exactOptional(),
});
export type PresenceEntry = z.infer<typeof PresenceEntry>;

/** GET /v1/presence/friends -- only people who are open or were active in the last 8 weeks. */
export const PresenceList = z.object({ items: z.array(PresenceEntry) });
export type PresenceList = z.infer<typeof PresenceList>;
